import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo } from 'react';
import { dayjs } from '../../config/dayjs';
import { QUERY_KEYS } from '../../constants';
import countries from '../../constants/countries.json';
import { createEditColumn } from '../../helpers/columns';
import { defaultColumnsMarketplace } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn } from '../../utils';
import Switch from '../atoms/Switch';
import TableTemplate from '../templates/TableTemplate';
import { Marketplace } from '../../@types/api';
import { Settings } from '@mui/icons-material';
import { STATUS } from '../../constants/enums';
import { APP_EVENT } from '../../constants/events';
import { Typography } from '@mui/material';
import { StyleObj } from '../../@types';

const styles: StyleObj = {
  country: {
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    textAlign: 'center',
  },
};

const MarketplacePage = () => {
  const {
    data: marketplaceData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<Marketplace>(
    'bo/marketplace',
    {
      page: 1,
      limit: 25,
    },
    {},
    'crm'
  );
  const { updateData } = useMutateData('bo/marketplace', [QUERY_KEYS.marketplace], 'crm');
  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('id', 'ID', {
        sortable: false,
      }),
      createColumn('name', 'Name'),
      createColumn('country', 'Country', {
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<Marketplace>) => {
          const country = countries.find((c) => c.cca2 === params.value);
          return <Typography sx={styles.country}>{country ? country.name : params.value}</Typography>;
        },
      }),
      createColumn('createdAt', 'Created at', {
        valueGetter: (value, row, column, apiRef) => dayjs(row.createdAt).format('DD-MM-YYYY HH:mm'),
      }),
      createColumn('contactName', 'Contact name', {
        valueGetter: (value, row, column, apiRef) => row.contactInformation.contactName,
        sortable: false,
      }),
      createColumn('status', 'Status', {
        renderCell: (params: GridRenderCellParams<Marketplace>) => {
          const value = params.row.status === STATUS.ACTIVE;
          return (
            <Switch
              value={value}
              onChange={(e) => {
                updateData(params.row.id, { status: e.target.checked ? STATUS.ACTIVE : STATUS.INACTIVE });
              }}
            />
          );
        },
      }),
      createEditColumn<Marketplace>({ columnName: 'Configure', icon: <Settings /> }),
    ],
    [updateData]
  );

  const handleTenantChange = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    window.addEventListener(APP_EVENT.TENANT_CHANGED, handleTenantChange);

    return () => {
      window.removeEventListener(APP_EVENT.TENANT_CHANGED, handleTenantChange);
    };
  }, [handleTenantChange]);

  return (
    <TableTemplate
      rows={marketplaceData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={marketplaceData?.count || 0}
      createItemBtnLabel='Add new marketplace'
      defaultVisibleColumns={defaultColumnsMarketplace}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      formType='marketplace'
      showExport
    />
  );
};

export default MarketplacePage;
